import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Tab,
  Tabs,
  Paper,
  Badge,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MedicalServices as MedicalIcon,
  LocalHospital as HospitalIcon,
  Star as StarIcon,
  CurrencyRupee as MoneyIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
  LocalOffer as TagIcon,
  Assignment as ServiceIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  ContentCut as SurgicalIcon,
  Psychology as SpecializedIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Import Firebase services
import { ServicesManagementService } from '../services/firebase';
import { collection, query, onSnapshot, doc, deleteDoc, getDocs, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Types
interface Service {
  id: string;
  name: string;
  description: string;
  category: 'medical' | 'surgical' | 'specialized';
  price: number;
  duration?: string;
  tags?: string[];
  doctorDetails?: Doctor[];
  isActive: boolean;
  createdAt: string;
}

interface Doctor {
  id: string;
  name: string;
  specialty: string;
  specialization: string;
  avatar?: string;
  experience?: string;
}

interface Category {
  id: 'medical' | 'surgical' | 'specialized';
  name: string;
  color: string;
  icon: React.ReactNode;
  description: string;
}

// Mock data based on your mobile app structure
const mockServices: Service[] = [
  {
    id: 'SVC-001',
    name: 'General Consultation',
    description: 'Comprehensive health checkup and consultation with experienced physicians',
    category: 'medical',
    price: 500,
    duration: '30-45 minutes',
    tags: ['Consultation', 'General Health', 'Primary Care'],
    doctorDetails: [
      { id: 'DOC-001', name: 'Ramesh Kumar', specialty: 'General Medicine', specialization: 'Internal Medicine' },
      { id: 'DOC-002', name: 'Priya Sharma', specialty: 'General Medicine', specialization: 'Family Medicine' }
    ],
    isActive: true,
    createdAt: '2025-10-15'
  },
  {
    id: 'SVC-002',
    name: 'Cardiology Consultation',
    description: 'Specialized heart care and cardiovascular assessment',
    category: 'medical',
    price: 800,
    duration: '45-60 minutes',
    tags: ['Heart Care', 'ECG', 'Cardiology'],
    doctorDetails: [
      { id: 'DOC-003', name: 'Priya Sharma', specialty: 'Cardiology', specialization: 'Interventional Cardiology' }
    ],
    isActive: true,
    createdAt: '2025-10-12'
  },
  {
    id: 'SVC-003',
    name: 'Orthopedic Surgery',
    description: 'Joint replacement and bone surgery procedures',
    category: 'surgical',
    price: 25000,
    duration: '2-4 hours',
    tags: ['Surgery', 'Joint Replacement', 'Orthopedics'],
    doctorDetails: [
      { id: 'DOC-002', name: 'Kani Priya', specialty: 'Orthopedics', specialization: 'Joint Replacement Surgery' }
    ],
    isActive: true,
    createdAt: '2025-10-10'
  },
  {
    id: 'SVC-004',
    name: 'ENT Surgery',
    description: 'Ear, nose, and throat surgical procedures',
    category: 'surgical',
    price: 15000,
    duration: '1-3 hours',
    tags: ['Surgery', 'ENT', 'Throat Surgery'],
    doctorDetails: [
      { id: 'DOC-004', name: 'Ajay Reddy', specialty: 'ENT', specialization: 'Head & Neck Surgery' }
    ],
    isActive: true,
    createdAt: '2025-10-08'
  },
  {
    id: 'SVC-005',
    name: 'ICU Care',
    description: 'Intensive care unit services with 24/7 monitoring',
    category: 'specialized',
    price: 5000,
    duration: 'Per day',
    tags: ['Critical Care', 'ICU', '24/7 Monitoring'],
    doctorDetails: [
      { id: 'DOC-001', name: 'Ramesh Kumar', specialty: 'Critical Care', specialization: 'Intensive Care Medicine' },
      { id: 'DOC-003', name: 'Priya Sharma', specialty: 'Critical Care', specialization: 'Emergency Medicine' }
    ],
    isActive: true,
    createdAt: '2025-10-05'
  },
  {
    id: 'SVC-006',
    name: 'Physiotherapy',
    description: 'Rehabilitation and physical therapy services',
    category: 'specialized',
    price: 300,
    duration: '45 minutes',
    tags: ['Rehabilitation', 'Physical Therapy', 'Recovery'],
    doctorDetails: [],
    isActive: true,
    createdAt: '2025-10-02'
  }
];

const mockDoctors: Doctor[] = [
  { id: 'DOC-001', name: 'Ramesh Kumar', specialty: 'General Medicine', specialization: 'Internal Medicine' },
  { id: 'DOC-002', name: 'Kani Priya', specialty: 'Orthopedics', specialization: 'Joint Replacement Surgery' },
  { id: 'DOC-003', name: 'Priya Sharma', specialty: 'Cardiology', specialization: 'Interventional Cardiology' },
  { id: 'DOC-004', name: 'Ajay Reddy', specialty: 'ENT', specialization: 'Head & Neck Surgery' }
];

const categories: Category[] = [
  {
    id: 'medical',
    name: 'Medical Specialties',
    color: '#4285F4',
    icon: <MedicalIcon />,
    description: 'General medical consultations and treatments'
  },
  {
    id: 'surgical',
    name: 'Surgical Specialties',
    color: '#EA4335',
    icon: <SurgicalIcon />,
    description: 'Surgical procedures and operations'
  },
  {
    id: 'specialized',
    name: 'Specialized Care',
    color: '#34A853',
    icon: <SpecializedIcon />,
    description: 'Specialized medical care and treatments'
  }
];

const Services: React.FC = () => {
  // State management
  const [services, setServices] = useState<Service[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'medical' | 'surgical' | 'specialized'>('medical');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);
  
  // Form state
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    category: 'medical' as 'medical' | 'surgical' | 'specialized',
    price: '',
    duration: '',
    tags: [''],
    assignedDoctors: [] as string[]
  });

  // Load services from Firebase with real-time updates
  useEffect(() => {
    const setupServicesData = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Setting up real-time services data from Firebase...');
        
        // Set up real-time Firebase listener for hospital services
        const q = query(collection(db, 'hospitalServices'));
        const unsubscribeFn = onSnapshot(q, (querySnapshot) => {
          const firebaseServices: Service[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            firebaseServices.push({
              id: doc.id,
              name: data.name || '',
              description: data.description || '',
              category: (data.category || 'medical') as 'medical' | 'surgical' | 'specialized',
              price: data.price || 0,
              duration: data.duration || '',
              tags: data.tags || [],
              doctorDetails: (data.assignedDoctors || []).map((doctorId: string) => ({
                id: doctorId,
                name: 'Dr. ' + doctorId.slice(0, 8),
                specialty: 'General',
                specialization: 'General Medicine'
              })),
              isActive: data.isActive !== false,
              createdAt: data.createdAt || new Date().toISOString()
            });
          });
          console.log('🏥 Received services update from Firebase:', firebaseServices.length);
          setServices(firebaseServices);
          setIsLoading(false);
          console.log('✅ Services data updated from Firebase');
        });
        
        setUnsubscribe(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up services data:', error);
        setIsLoading(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load services data', 
          severity: 'error' 
        });
      }
    };

    setupServicesData();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('🧹 Cleaning up services listener...');
        unsubscribe();
      }
    };
  }, []);

  // Load doctors from Firebase
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        console.log('👨‍⚕️ Fetching doctors from Firebase...');
        const doctorsQuery = query(collection(db, 'doctors'));
        const doctorsSnapshot = await getDocs(doctorsQuery);
        const doctorsData: Doctor[] = [];
        
        doctorsSnapshot.forEach((doc) => {
          const data = doc.data();
          doctorsData.push({
            id: doc.id,
            name: data.name || '',
            specialty: data.specialty || data.department || '',
            specialization: data.specialization || data.specialty || '',
            avatar: data.avatar || data.profileImage || '',
            experience: data.experience || '0'
          });
        });
        
        console.log('✅ Loaded doctors:', doctorsData.length);
        setDoctors(doctorsData);
      } catch (error) {
        console.error('❌ Error fetching doctors:', error);
      }
    };

    fetchDoctors();
  }, []);

  // Calculate statistics
  const serviceStats = useMemo(() => {
    const safeServices = services || [];
    const totalServices = safeServices.length;
    const activeServices = safeServices.filter(s => s.isActive).length;
    const medicalServices = safeServices.filter(s => s.category === 'medical').length;
    const surgicalServices = safeServices.filter(s => s.category === 'surgical').length;
    const specializedServices = safeServices.filter(s => s.category === 'specialized').length;
    const assignedDoctors = new Set(safeServices.flatMap(s => s.doctorDetails?.map(d => d.id) || [])).size;
    
    return {
      totalServices,
      activeServices,
      medicalServices,
      surgicalServices,
      specializedServices,
      assignedDoctors
    };
  }, [services]);

  // Get service counts by category
  const getServiceCounts = () => {
    const counts = { medical: 0, surgical: 0, specialized: 0 };
    (services || []).forEach(service => {
      if (counts.hasOwnProperty(service.category)) {
        counts[service.category]++;
      }
    });
    return counts;
  };

  const serviceCounts = getServiceCounts();

  // Filtered services
  const filteredServices = useMemo(() => {
    if (!services || !Array.isArray(services)) {
      return [];
    }
    return services.filter(service => {
      const matchesSearch = !searchQuery || 
                           service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           service.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = service.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [services, searchQuery, selectedCategory]);

  // Refresh handler - resets filters and shows all services
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      
      // Reset filters to show all data
      setSearchQuery('');
      setSelectedCategory('medical');
      
      // The real-time listener will automatically update the data
      setTimeout(() => {
        setIsRefreshing(false);
        setSnackbar({ 
          open: true, 
          message: 'Services data refreshed successfully!', 
          severity: 'success' 
        });
      }, 500);
    } catch (error) {
      console.error('❌ Error refreshing:', error);
      setIsRefreshing(false);
      setSnackbar({ 
        open: true, 
        message: 'Failed to refresh services data', 
        severity: 'error' 
      });
    }
  };

  // Utility functions
  const resetForm = () => {
    setServiceForm({
      name: '',
      description: '',
      category: 'medical',
      price: '',
      duration: '',
      tags: [''],
      assignedDoctors: []
    });
  };

  const addTag = () => {
    setServiceForm(prev => ({
      ...prev,
      tags: [...prev.tags, '']
    }));
  };

  const updateTag = (index: number, value: string) => {
    setServiceForm(prev => ({
      ...prev,
      tags: prev.tags.map((tag, i) => i === index ? value : tag)
    }));
  };

  const removeTag = (index: number) => {
    setServiceForm(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  // Handler functions
  const handleAddService = async () => {
    if (!serviceForm.name || !serviceForm.description || !serviceForm.duration) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      console.log('➕ Adding new service to Firebase...');
      
      const serviceData = {
        name: serviceForm.name,
        description: serviceForm.description,
        category: serviceForm.category,
        duration: serviceForm.duration,
        tags: serviceForm.tags.filter(tag => tag.trim() !== ''),
        assignedDoctors: serviceForm.assignedDoctors,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add to Firebase hospitalServices collection
      const docRef = await addDoc(collection(db, 'hospitalServices'), serviceData);
      
      setShowAddModal(false);
      resetForm();
      setSnackbar({ open: true, message: 'Service added successfully!', severity: 'success' });
      console.log('✅ Service added successfully:', docRef.id);
    } catch (error: any) {
      console.error('❌ Error adding service:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to add service', 
        severity: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditService = (service: Service) => {
    setSelectedService(service);
    setServiceForm({
      name: service.name,
      description: service.description,
      category: service.category,
      price: service.price.toString(),
      duration: service.duration || '',
      tags: service.tags || [''],
      assignedDoctors: service.doctorDetails?.map(d => d.id) || []
    });
    setShowEditModal(true);
  };

  const handleUpdateService = async () => {
    if (!selectedService) return;

    setIsLoading(true);
    try {
      console.log('🔄 Updating service in Firebase:', selectedService.id);
      
      const updateData = {
        name: serviceForm.name,
        description: serviceForm.description,
        category: serviceForm.category,
        duration: serviceForm.duration,
        tags: serviceForm.tags.filter(tag => tag.trim() !== ''),
        assignedDoctors: serviceForm.assignedDoctors,
        updatedAt: new Date().toISOString()
      };
      
      // Update in Firebase
      const serviceRef = doc(db, 'hospitalServices', selectedService.id);
      await updateDoc(serviceRef, updateData);
      
      setShowEditModal(false);
      resetForm();
      setSelectedService(null);
      
      setSnackbar({ open: true, message: 'Service updated successfully!', severity: 'success' });
      console.log('✅ Service updated successfully in Firebase');
    } catch (error: any) {
      console.error('❌ Error updating service:', error);
      setSnackbar({ open: true, message: 'Failed to update service', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewService = (service: Service) => {
    setSelectedService(service);
    setShowViewModal(true);
  };

  const handleDeleteService = async (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;
    
    if (window.confirm(`Are you sure you want to permanently delete ${service.name}? This action cannot be undone.`)) {
      try {
        console.log('🗑️ Deleting service from Firebase:', serviceId);
        
        // Delete from Firebase hospitalServices collection
        const serviceRef = doc(db, 'hospitalServices', serviceId);
        await deleteDoc(serviceRef);
        
        setSnackbar({ open: true, message: `${service.name} has been permanently deleted`, severity: 'success' });
        console.log('✅ Service deleted successfully from web dashboard');
      } catch (error: any) {
        console.error('❌ Error deleting service:', error);
        setSnackbar({ open: true, message: `Failed to delete service: ${error.message || 'Unknown error'}`, severity: 'error' });
      }
    }
  };

  const handleAssignDoctor = (service: Service) => {
    setSelectedService(service);
    setServiceForm(prev => ({
      ...prev,
      assignedDoctors: service.doctorDetails?.map(d => d.id) || []
    }));
    setShowAssignModal(true);
  };

  const handleAssignmentSubmit = async () => {
    if (!selectedService) return;

    setIsLoading(true);
    try {
      console.log('👨‍⚕️ Updating doctor assignments in Firebase:', selectedService.id);
      
      const updateData = {
        assignedDoctors: serviceForm.assignedDoctors,
        updatedAt: new Date().toISOString()
      };
      
      // Update in Firebase
      const serviceRef = doc(db, 'hospitalServices', selectedService.id);
      await updateDoc(serviceRef, updateData);
      
      setShowAssignModal(false);
      setSelectedService(null);
      
      setSnackbar({ open: true, message: 'Doctor assignments updated successfully!', severity: 'success' });
      console.log('✅ Doctor assignments updated successfully in Firebase');
    } catch (error: any) {
      console.error('❌ Error updating doctor assignments:', error);
      setSnackbar({ open: true, message: 'Failed to update assignments', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Component renders
  const StatCard: React.FC<{ title: string; value: string | number; color: string; icon: React.ReactNode; subtitle?: string }> = ({ 
    title, value, color, icon, subtitle 
  }) => (
    <Card sx={{ 
      backgroundColor: color,
      color: 'white',
      borderRadius: 3,
      minHeight: 120
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '24px', lineHeight: 1 }}>
              {typeof value === 'number' && title.includes('Revenue') ? `₹${value.toLocaleString()}` : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ fontSize: '12px', opacity: 0.8, mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '14px' }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const ServiceCard: React.FC<{ service: Service }> = ({ service }) => {
    const categoryInfo = categories.find(c => c.id === service.category);
    
    return (
      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          {/* Service Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                bgcolor: categoryInfo?.color + '15', 
                color: categoryInfo?.color,
                width: 50, 
                height: 50 
              }}>
                {categoryInfo?.icon}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                  {service.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                  {service.id} • {categoryInfo?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', mt: 0.5 }}>
                  {service.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
              <Chip 
                label={service.isActive ? 'Active' : 'Inactive'} 
                sx={{ backgroundColor: service.isActive ? '#10B981' : '#EF4444', color: 'white', fontWeight: 'bold' }}
                size="small"
              />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: categoryInfo?.color }}>
                ₹{service.price.toLocaleString()}
              </Typography>
            </Box>
          </Box>
          
          {/* Service Details */}
          <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Duration</Typography>
              <Typography variant="body2">{service.duration || 'Not specified'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold' }}>Assigned Doctors</Typography>
              <Typography variant="body2">{service.doctorDetails?.length || 0} doctor(s)</Typography>
            </Box>
          </Box>

          {/* Assigned Doctors */}
          {service.doctorDetails && service.doctorDetails.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold', mb: 1 }}>
                Doctors ({service.doctorDetails.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {service.doctorDetails.map((doctor, index) => (
                  <Chip 
                    key={index}
                    avatar={<Avatar sx={{ bgcolor: '#4A90E2' }}>{doctor.name.charAt(0)}</Avatar>}
                    label={`Dr. ${doctor.name}`}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Tags */}
          {service.tags && service.tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold', mb: 1 }}>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {service.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<ViewIcon />}
              onClick={() => handleViewService(service)}
              sx={{ color: '#007AFF' }}
            >
              View
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditService(service)}
              sx={{ color: '#8A2BE2' }}
            >
              Edit
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => handleAssignDoctor(service)}
              sx={{ color: '#34C759' }}
            >
              Assign
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handleDeleteService(service.id)}
              sx={{ color: '#EF4444' }}
            >
              Delete
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Service Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Add, edit, or remove hospital services and specialties
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
        gap: 3,
        mb: 4 
      }}>
        <StatCard 
          title="Total Services" 
          value={serviceStats.totalServices} 
          color="#4A90E2" 
          icon={<ServiceIcon />}
          subtitle="All available services"
        />
        <StatCard 
          title="Active Services" 
          value={serviceStats.activeServices} 
          color="#10B981" 
          icon={<TrendingUpIcon />}
          subtitle="Currently available"
        />
        <StatCard 
          title="Assigned Doctors" 
          value={serviceStats.assignedDoctors} 
          color="#8A2BE2" 
          icon={<GroupIcon />}
          subtitle="Doctors with services"
        />
      </Box>

      {/* Category Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 3 }}>
        <Tabs 
          value={selectedCategory} 
          onChange={(e, newValue) => setSelectedCategory(newValue)}
          variant="fullWidth"
          sx={{ 
            '& .MuiTab-root': { 
              minHeight: 80,
              flexDirection: 'column',
              gap: 1
            }
          }}
        >
          {categories.map((category) => (
            <Tab 
              key={category.id}
              value={category.id}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {category.name}
                    </Typography>
                  </Box>
                  <Badge 
                    badgeContent={serviceCounts[category.id] || 0} 
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { fontSize: '10px' } }}
                  >
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Services
                    </Typography>
                  </Badge>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Search and Actions */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search services by name, description, or tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: '#9CA3AF' }} />,
          }}
          sx={{ flex: 1 }}
        />
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{ 
            backgroundColor: '#10B981', 
            '&:hover': { backgroundColor: '#059669' },
            borderRadius: 3,
            minWidth: 200
          }}
        >
          Add New Service
        </Button>
        
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Services List */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredServices.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {categories.find(c => c.id === selectedCategory)?.icon}
              <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                No Services in {categories.find(c => c.id === selectedCategory)?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                {searchQuery 
                  ? 'Try adjusting your search criteria'
                  : `Start by adding services to the ${categories.find(c => c.id === selectedCategory)?.name} category`
                }
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddModal(true)}
                  sx={{ backgroundColor: '#10B981', '&:hover': { backgroundColor: '#059669' } }}
                >
                  Add First Service
                </Button>
              )}
            </Box>
          </Card>
        ) : (
          (filteredServices || []).map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))
        )}
      </Box>

      {/* Add/Edit Service Modal */}
      <Dialog
        open={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{showEditModal ? 'Edit Service' : 'Add New Service'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Service Name */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                Service Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter service name"
                value={serviceForm.name}
                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                variant="outlined"
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                Description *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter service description"
                multiline
                rows={3}
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                variant="outlined"
              />
            </Box>

            {/* Duration */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                Duration *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., 30 mins"
                value={serviceForm.duration}
                onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })}
                variant="outlined"
              />
            </Box>

            {/* Category Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                Select Service Type *
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {categories.map((category) => (
                  <Paper
                    key={category.id}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      border: serviceForm.category === category.id ? '2px solid #4A90E2' : '1px solid #E0E0E0',
                      backgroundColor: serviceForm.category === category.id ? '#F0F8FF' : 'white',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#4A90E2',
                        backgroundColor: '#F9FAFB'
                      }
                    }}
                    onClick={() => setServiceForm({ ...serviceForm, category: category.id })}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: category.color,
                      }}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: serviceForm.category === category.id ? 600 : 400,
                        color: serviceForm.category === category.id ? '#4A90E2' : '#333'
                      }}
                    >
                      {category.name}
                    </Typography>
                    {serviceForm.category === category.id && (
                      <Box sx={{ color: '#4A90E2', display: 'flex', alignItems: 'center' }}>
                        ✓
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Assign Doctors */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 0.5 }}>
                Assign Doctors *
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block' }}>
                Select doctors who specialize in this service
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
                {doctors && doctors.length > 0 ? (
                  doctors.map((doctor) => (
                    <Paper
                      key={doctor.id}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                        border: serviceForm.assignedDoctors.includes(doctor.id) ? '2px solid #4A90E2' : '1px solid #E0E0E0',
                        backgroundColor: serviceForm.assignedDoctors.includes(doctor.id) ? '#F0F8FF' : 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#4A90E2',
                          backgroundColor: '#F9FAFB'
                        }
                      }}
                      onClick={() => {
                        const isSelected = serviceForm.assignedDoctors.includes(doctor.id);
                        setServiceForm({
                          ...serviceForm,
                          assignedDoctors: isSelected
                            ? serviceForm.assignedDoctors.filter(id => id !== doctor.id)
                            : [...serviceForm.assignedDoctors, doctor.id]
                        });
                      }}
                    >
                      <Avatar
                        src={doctor.avatar}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#4A90E2',
                          fontSize: '16px',
                          fontWeight: 600
                        }}
                      >
                        {doctor.name?.charAt(0)?.toUpperCase() || 'D'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: serviceForm.assignedDoctors.includes(doctor.id) ? 600 : 400,
                            color: serviceForm.assignedDoctors.includes(doctor.id) ? '#4A90E2' : '#333',
                            fontSize: '14px'
                          }}
                        >
                          Dr. {doctor.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          {doctor.specialty || doctor.specialization}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {doctor.experience || '0'} years experience
                        </Typography>
                      </Box>
                      {serviceForm.assignedDoctors.includes(doctor.id) && (
                        <Box sx={{ color: '#4A90E2', display: 'flex', alignItems: 'center' }}>
                          ✓
                        </Box>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3, color: '#666' }}>
                    <PersonAddIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                    <Typography variant="body2">
                      No doctors available. Please add doctors first.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Service Tags */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600, mb: 0.5 }}>
                Service Tags (Optional)
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block' }}>
                Comma-separated tags (e.g., Health Check-ups, Emergency Medicine)
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter tags separated by commas"
                multiline
                rows={2}
                value={serviceForm.tags.join(', ')}
                onChange={(e) => {
                  const tagsArray = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                  setServiceForm({ ...serviceForm, tags: tagsArray });
                }}
                variant="outlined"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              resetForm();
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#E0E0E0', 
              color: '#666',
              textTransform: 'none',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={showEditModal ? handleUpdateService : handleAddService} 
            variant="contained" 
            disabled={isLoading}
            sx={{ 
              backgroundColor: '#4A90E2',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#357ABD'
              }
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : showEditModal ? 'Update Service' : 'Add Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Doctor Modal */}
      <Dialog
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assign Doctors to {selectedService?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              Select doctors to assign to this service. Assigned doctors will be able to provide this service.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Select Doctors</InputLabel>
              <Select
                multiple
                value={serviceForm.assignedDoctors}
                onChange={(e) => setServiceForm({ ...serviceForm, assignedDoctors: e.target.value as string[] })}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((doctorId) => {
                      const doctor = doctors.find(d => d.id === doctorId);
                      return (
                        <Chip key={doctorId} label={`Dr. ${doctor?.name}`} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {doctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#4A90E2', width: 32, height: 32 }}>
                        {doctor.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Dr. {doctor.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {doctor.specialization}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignModal(false)}>Cancel</Button>
          <Button onClick={handleAssignmentSubmit} variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : 'Update Assignments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Service Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Service Details</DialogTitle>
        <DialogContent>
          {selectedService && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3 
              }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Service Information</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {selectedService.name}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Category:</strong> {categories.find(c => c.id === selectedService.category)?.name}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Price:</strong> ₹{selectedService.price.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Duration:</strong> {selectedService.duration || 'Not specified'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Status:</strong> {selectedService.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Description</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedService.description}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2 }}>Created</Typography>
                  <Typography variant="body2">
                    {selectedService.createdAt}
                  </Typography>
                </Box>
              </Box>
                
              {selectedService.doctorDetails && selectedService.doctorDetails.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Assigned Doctors ({selectedService.doctorDetails.length})</Typography>
                  <List>
                    {selectedService.doctorDetails.map((doctor, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#4A90E2' }}>
                            {doctor.name.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`Dr. ${doctor.name}`}
                          secondary={doctor.specialization}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
                
              {selectedService.tags && selectedService.tags.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedService.tags.map((tag, index) => (
                      <Chip key={index} label={tag} color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)}>Close</Button>
          {selectedService && (
            <Button onClick={() => handleEditService(selectedService)} variant="contained">
              Edit Service
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;